"use client";

import { cx } from "class-variance-authority";
import Image from "next/image";
import { useEffect, useState } from "react";
import { FALLBACK_IMG_SRC } from "@/00-shared/const";
import { Modal, ModalContent, ModalBody, useDisclosure, Button } from "@nextui-org/react";
import { isArray } from "lodash";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
type ImageProps = {
  src?: string;
  className?: string;
  modalClassName?: string;
  originalImg?: string | string[];
  fallbackImg?: string;
  fitObject?: boolean;
  disableZoom?: boolean;
  onClick?: () => void;
  modalStyle?: React.CSSProperties;
};
export const ImageComp = ({
  src,
  className,
  originalImg,
  fallbackImg = FALLBACK_IMG_SRC,
  fitObject = true,
  disableZoom = false,
  modalClassName,
  onClick,
  modalStyle
}: ImageProps) => {
  const [imgSrc, setImgSrc] = useState(src || "");
  const originalPropImg = originalImg || src;
  const [originalImgSrc, setOriginalImgSrc] = useState(originalPropImg || "");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("right");
  useEffect(() => {
    setOriginalImgSrc(originalPropImg || "");
  }, [originalPropImg]);
  const {
    isOpen,
    onOpen,
    onOpenChange
  } = useDisclosure();
  useEffect(() => {
    setImgSrc(src ?? "");
  }, [src]);
  const handlePrevImage = () => {
    if (isArray(originalImgSrc)) {
      setDirection("left");
      setCurrentImageIndex(prev => prev === 0 ? originalImgSrc.length - 1 : prev - 1);
    }
  };
  const handleNextImage = () => {
    if (isArray(originalImgSrc)) {
      setDirection("right");
      setCurrentImageIndex(prev => prev === originalImgSrc.length - 1 ? 0 : prev + 1);
    }
  };
  const variants = {
    enter: (direction: "left" | "right") => ({
      x: direction === "right" ? "100%" : "-100%"
    }),
    center: {
      x: 0
    },
    exit: (direction: "left" | "right") => ({
      x: direction === "right" ? "-100%" : "100%"
    })
  };
  return <>
      <div className={cx("relative flex size-14 shrink-0  items-center justify-center overflow-hidden rounded border border-border", className, !disableZoom && "cursor-zoom-in")} onClick={e => {
      if (onClick) {
        onClick();
        return;
      }
      onOpen();
      e.preventDefault();
      e.stopPropagation();
    }} onTouchStart={e => {
      onOpen();
      e.preventDefault();
      e.stopPropagation();
    }}>
        <Image key={imgSrc} placeholder="blur" blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/uPXfwAJwQPqliHzvQAAAABJRU5ErkJggg==" className={cx(fitObject && "object-contain", "my-0")} fill alt="img" onError={() => setImgSrc(fallbackImg)} src={imgSrc} data-sentry-element="Image" data-sentry-source-file="ImageComp.tsx" />
      </div>
      {!disableZoom && <div
    // 避免文档流排版问题
    className="absolute" onClick={e => {
      // 避免 modal 组件触发外层容器组件的 click 事件
      e.stopPropagation();
    }}>
          <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
            <ModalContent>
              <ModalBody>
                <div className={cx("relative m-auto mt-4 size-96 overflow-hidden", className, modalClassName)} style={modalStyle}>
                  {isArray(originalImgSrc) ? <div className="relative size-full">
                      <AnimatePresence initial={false} mode="sync" custom={direction}>
                        <motion.div key={currentImageIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.3
                  }} className="absolute size-full">
                          <Image key={originalImgSrc[currentImageIndex]} placeholder="blur" blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/uPXfwAJwQPqliHzvQAAAABJRU5ErkJggg==" className="object-contain" fill alt="img" onError={() => setOriginalImgSrc(fallbackImg)} src={originalImgSrc[currentImageIndex]} />
                        </motion.div>
                      </AnimatePresence>
                      {originalImgSrc.length > 1 && <>
                          <Button isIconOnly className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/30" onPress={handlePrevImage}>
                            <ChevronLeftIcon className="size-6 text-white" />
                          </Button>
                          <Button isIconOnly className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/30" onPress={handleNextImage}>
                            <ChevronRightIcon className="size-6 text-white" />
                          </Button>
                          <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-2">
                            {Array.from({
                      length: originalImgSrc.length
                    }).map((_, index) => <div key={index} className={cx("size-2 rounded-full transition-colors", index === currentImageIndex ? "bg-primary" : "bg-default-300")} />)}
                          </div>
                        </>}
                    </div> : <Image key={originalImgSrc} placeholder="blur" blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/uPXfwAJwQPqliHzvQAAAABJRU5ErkJggg==" className="object-contain" fill alt="img" onError={() => setOriginalImgSrc(fallbackImg)} src={originalImgSrc} />}
                </div>
              </ModalBody>
            </ModalContent>
          </Modal>
        </div>}
    </>;
};